import Script from "next/script";

import { isTrackingEnabled } from "@/lib/analytics/tracking-gate";
import { OPENAI_PIXEL_ID, TWITTER_PIXEL_ID } from "@/utils/constants";

export const AnalyticsConfig = {
  googleAnalytics: {
    trackingId: process.env.NEXT_PUBLIC_GA_TRACKING_ID || "",
    gtag: process.env.NEXT_PUBLIC_GA_GTAG || "",
  },
  facebookPixel: {
    pixelId: process.env.NEXT_PUBLIC_FB_PIXEL_ID || "",
  },
  googleTagManager: {
    containerId: process.env.NEXT_PUBLIC_GTM_CONTAINER_ID || "",
  },
  twitterPixel: {
    pixelId: process.env.NEXT_PUBLIC_TWITTER_PIXEL_ID || TWITTER_PIXEL_ID,
  },
  openAIPixel: {
    pixelId: process.env.NEXT_PUBLIC_OPENAI_PIXEL_ID || OPENAI_PIXEL_ID,
  },
};

// GTM Head Component
export const GoogleTagManagerHead = () => {
  if (!isTrackingEnabled()) {
    return null;
  }

  return (
    <Script
      id="gtm-script"
      strategy="beforeInteractive"
      dangerouslySetInnerHTML={{
        __html: `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${AnalyticsConfig.googleTagManager.containerId}');
        `,
      }}
    />
  );
};

// GTM Body Component
export const GoogleTagManagerBody = () => {
  if (!isTrackingEnabled()) {
    return null;
  }

  return (
    <noscript>
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${AnalyticsConfig.googleTagManager.containerId}`}
        height="0"
        width="0"
        style={{ display: "none", visibility: "hidden" }}
        title="Google Tag Manager"
      />
    </noscript>
  );
};

// GA4 Component
export const GoogleAnalytics = () => {
  if (!isTrackingEnabled()) {
    return null;
  }

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${AnalyticsConfig.googleAnalytics.trackingId}`}
        strategy="afterInteractive"
      />
      <Script
        id="ga-script"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${AnalyticsConfig.googleAnalytics.trackingId}', {
              page_path: window.location.pathname,
              send_page_view: true
            });
            gtag('config', '${AnalyticsConfig.googleAnalytics.gtag}');
          `,
        }}
      />
    </>
  );
};

// Facebook Pixel Component
export const FacebookPixel = () => {
  if (!isTrackingEnabled()) {
    return null;
  }

  return (
    <>
      <Script
        id="fb-pixel"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${AnalyticsConfig.facebookPixel.pixelId}');
            fbq('track', 'PageView');
          `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src={`https://www.facebook.com/tr?id=${AnalyticsConfig.facebookPixel.pixelId}&ev=PageView&noscript=1`}
          alt=""
          role="presentation"
        />
      </noscript>
    </>
  );
};

// X (Twitter) conversion tracking pixel
export const TwitterPixel = () => {
  const { pixelId } = AnalyticsConfig.twitterPixel;

  if (!isTrackingEnabled() || !pixelId) {
    return null;
  }

  return (
    <Script
      id="twitter-pixel"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `
          !function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
          },s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',
          a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
          twq('config','${pixelId}');
        `,
      }}
    />
  );
};

// OpenAI Ads pixel
export const OpenAIPixel = () => {
  const { pixelId } = AnalyticsConfig.openAIPixel;

  if (!isTrackingEnabled() || !pixelId) {
    return null;
  }

  const debug = process.env.NODE_ENV !== "production";

  return (
    <Script
      id="openai-pixel"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `
          !function(w,d,s,u){if(w.oaiq)return;var q=function(){q.q.push(arguments)};q.q=[];w.oaiq=q;var j=d.createElement(s);j.async=1;j.src=u;var f=d.getElementsByTagName(s)[0];f.parentNode.insertBefore(j,f)}(window,document,"script","https://bzrcdn.openai.com/sdk/oaiq.min.js");
          oaiq("init",{pixelId:"${pixelId}",debug:${debug}});
        `,
      }}
    />
  );
};
